import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { GRAY_LIGHT, GRAY_SECONDARY, PRIMARY } from 'constants/colors';

import type { RadioGroupProps, RadioProps } from '@mui/material';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  styled
} from '@mui/material';

const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiTypography-root {
    border-radius: 16px;
    background: ${GRAY_LIGHT};
    padding: 4px 8px;
    color: ${GRAY_SECONDARY};
    font-family: SourceSansPro-Regular;
  }

  .Mui-checked + .MuiTypography-root {
    color: white;
    background: ${PRIMARY};
  }

  .MuiRadio-root {
    padding: 0;
    display: none;
  }

  .Mui-disabled + .MuiTypography-root {
    background: ${GRAY_LIGHT};
    color: ${GRAY_SECONDARY};
  }
`;

type Option = {
  label: string;
  value: string;
};

type Props = Omit<RadioGroupProps, 'defaultValue'> &
  Omit<RadioProps, 'name'> & {
    control: Control<any>;
    name: string;
    label: string;
    options: Option[];
    direction?: 'row' | 'column';
    inline?: boolean;
  };

export default function ControlledRadioGroup({
  control,
  name,
  label,
  row,
  direction = 'column',
  options,
  disabled = false,
  inline = false
}: Props) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl
          error={!!fieldState.error}
          sx={
            inline
              ? {
                  display: 'flex',
                  width: '100%',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2
                }
              : {}
          }
        >
          <FormLabel
            sx={{
              color: 'text.primary',
              ml: -1,
              ...(inline ? { minWidth: 160, flexShrink: 0 } : {})
            }}
          >
            {label}
          </FormLabel>
          <RadioGroup
            {...field}
            row={row}
            sx={{
              mt: inline ? 0 : 1,
              gap: inline ? 1.5 : 3,
              flexDirection: direction,
              flexWrap: 'wrap'
            }}
          >
            {options
              .filter((option) => !disabled || option.value === field.value)
              .map((option) => (
                <StyledFormControlLabel
                  disabled={disabled}
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
          </RadioGroup>
          <FormHelperText sx={{ ml: 0 }}>
            {fieldState?.error?.message}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
}
